import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { usePageParam } from "@/hooks/use-page-param";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ListPagination } from "@/components/ui/list-pagination";
import { PageHeader } from "@/components/ui/page-header";
import { useToast } from "@/hooks/use-toast";
import {
  Search,
  UserPlus,
  RefreshCw,
  Grid3x3,
  Rows3,
  List,
  Filter,
  Target,
} from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { KanbanAddOrigin, useKanbanApi } from "@/utils/kanban-api";
import type { KanbanEtapa } from "@/types/kanban";
import {
  rejectedCandidatesNotice,
  classifyVagaSelection,
  getEtapaDeEntrada,
  useEnviarFreelancersParaVaga,
  type FreelancerParaVaga,
} from "@/hooks/use-enviar-freelancers-para-vaga";
import { useAuth } from "@/contexts/AuthContext";
import { FreelancerListItem } from "@/types/freelancer";
import { useFreelancersViewMode } from "@/hooks/use-freelancers-view-mode";
import { useFreelancersData } from "@/hooks/use-freelancers-data";
import { useDisponibilidadeBatch } from "@/hooks/useDisponibilidade";
import { useFreelancerSearchMatch } from "@/hooks/use-freelancer-search-match";
import { DisponibilidadeCountChips } from "@/components/freelancers/DisponibilidadeCountChips";
import {
  SelectionContext,
  useFreelancersSelection,
} from "@/hooks/use-freelancers-selection";
import { FreelancerSortOption } from "@/hooks/use-freelancers-filter-sort";

import { FreelancerCardV2 } from "@/components/freelancers/v2/FreelancerCardV2";
import { FreelancerCardWide } from "@/components/freelancers/v2/FreelancerCardWide";
import { FreelancerListTable } from "@/components/freelancers/v2/FreelancerListRow";
import { FreelancerFilterChips } from "@/components/freelancers/v2/FreelancerFilterChips";
import {
  FreelancerFiltersPanel,
  type FiltersPanelMode,
} from "@/components/freelancers/v2/FreelancerFiltersPanel";
import { FreelancersVagaMatch } from "@/components/freelancers/v2/FreelancersVagaMatch";
import { FreelancerMultiSelectBar } from "@/components/freelancers/v2/FreelancerMultiSelectBar";
import { FreelancerCompareDrawer } from "@/components/freelancers/v2/FreelancerCompareDrawer";
import { NovoFreelancerDialog } from "@/components/freelancers/NovoFreelancerDialog";
import { EnviarParaVagaDialog } from "@/components/vagas/EnviarParaVagaDialog";
import { FilterValues, SEARCH_EXACT_FILTER, countActiveFilters } from "@/components/freelancers/v2/filterUtils";
import { externalUrlHref, openExternalUrl, parseLinkedInUrl } from "@/utils/external-url";
import { trackFreelancerView } from "@/hooks/use-track-freelancer-view";
import { useTrackFreelancerSearch } from "@/hooks/use-track-freelancer-search";
import { filterValueProperties } from "@/lib/posthog";
import { filterChanges, toSlug, trackEvent } from "@/lib/telemetry";
import { useFreelancerResumoRapido } from "@/hooks/use-freelancer-resumo-rapido";
import { useSidePanel } from "@/components/patterns";
import { CandidatoPanel, type CandidatoPanelTab } from "@/components/freelancers/painel/CandidatoPanel";
import { useRegistrarPainelDaPagina } from "@/components/freelancers/painel/painel-do-candidato";
import { cn } from "@/lib/utils";

function activeFilterNames(filters: FilterValues): string[] {
  return Object.entries(filters)
    .filter(([, value]) => value !== null && value !== undefined && value !== "" && !(Array.isArray(value) && value.length === 0))
    .map(([key]) => toSlug(key))
    .filter((name): name is string => !!name)
    .slice(0, 12);
}

const FILTERS_STORAGE_KEY = "freelancer_filters";
const FILTERS_PANEL_OPEN_KEY = "freelancers.filters_panel_open";
const FILTERS_PANEL_MODE_KEY = "freelancers.filters_panel_mode";
const SORT_STORAGE_KEY = "freelancers.sort_option_v2";
const PAGE_SIZE_KEY = "freelancers.page_size_v2";
const SEARCH_TERM_KEY = "freelancers_search_term";
const SEARCH_ONLY_NAME_KEY = "freelancers_search_only_name";

const SORT_LABELS: Record<FreelancerSortOption, string> = {
  score_desc: "Score de reputação ↓",
  aprovacoes_desc: "Mais aprovações ↓",
  shortlists_desc: "Mais shortlists ↓",
  projetos_concluidos_desc: "Mais projetos concluídos ↓",
  conversao_desc: "Maior taxa de conversão ↓",
  premios_desc: "Mais prêmios ↓",
  media_perfil_desc: "Maior média de perfil ↓",
  media_perfil_asc: "Menor média de perfil ↑",
  media_projeto_desc: "Maior média de projeto ↓",
  media_projeto_asc: "Menor média de projeto ↑",
  maior_nota_desc: "Maior nota geral ↓",
  rating_desc: "Avaliação interna ↓",
  rating_asc: "Avaliação interna ↑",
  updated_desc: "Atualização mais recente ↓",
  updated_asc: "Atualização mais antiga ↑",
  created_desc: "Cadastro mais recente ↓",
  created_asc: "Cadastro mais antigo ↑",
};

export default function FreelancersV2() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const kanbanApi = useKanbanApi();
  const { enviar: enviarParaVaga, reactivate: reactivateInVaga } = useEnviarFreelancersParaVaga();
  const { user } = useAuth();

  const vagaId = searchParams.get("vaga");
  const evaluationGroupId = searchParams.get("evaluationGroup");
  const action = searchParams.get("action"); // "evaluation" ou null

  const { mode: viewMode, setMode: setViewMode } = useFreelancersViewMode();

  const [novoFreelancerOpen, setNovoFreelancerOpen] = useState(false);

  const selectionCtx: SelectionContext = useMemo(
    () =>
      vagaId
        ? { kind: "vaga", id: vagaId }
        : evaluationGroupId
          ? { kind: "evaluationGroup", id: evaluationGroupId }
          : { kind: "global" },
    [vagaId, evaluationGroupId],
  );
  const selection = useFreelancersSelection(selectionCtx);

  const [searchTerm, setSearchTerm] = useState(
    () => sessionStorage.getItem(SEARCH_TERM_KEY) ?? "",
  );
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [searchOnlyName, setSearchOnlyName] = useState(
    () => localStorage.getItem(SEARCH_ONLY_NAME_KEY) === "true",
  );

  const [filters, setFilters] = useState<FilterValues>(() => {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    let parsed: FilterValues = {};
    if (raw) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = {};
      }
    }
    // Em modo "vincular candidatos a uma vaga", mostramos tambem os inativos
    // (marcados como "inativo") para que possam ser adicionados ao kanban. Fora
    // disso, mantemos o padrao de exibir apenas ativos. O preferido salvo em
    // localStorage e do modo normal, entao nao o aplicamos no modo vaga.
    if (vagaId) {
      return { ...parsed, is_active: "" };
    }
    return { ...parsed, is_active: parsed.is_active ?? "true" };
  });

  const [sortOption, setSortOption] = useState<FreelancerSortOption>(() => {
    const saved = localStorage.getItem(SORT_STORAGE_KEY);
    return (saved as FreelancerSortOption | null) ?? "score_desc";
  });

  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = localStorage.getItem(PAGE_SIZE_KEY);
    return saved ? Math.max(6, parseInt(saved, 10)) : 12;
  });

  const [currentPage, setCurrentPage] = usePageParam();

  const [filtersPanelOpen, setFiltersPanelOpen] = useState<boolean>(
    () => localStorage.getItem(FILTERS_PANEL_OPEN_KEY) !== "false",
  );

  const handleToggleFiltersPanel = useCallback(() => {
    setFiltersPanelOpen((open) => {
      localStorage.setItem(FILTERS_PANEL_OPEN_KEY, String(!open));
      return !open;
    });
  }, []);

  // Posição do painel de filtros: flutuante à direita (sobre a listagem),
  // encaixado (coluna ao lado) ou sobre o menu à esquerda. Alternável no cabeçalho.
  const [panelMode, setPanelMode] = useState<FiltersPanelMode>(() => {
    const saved = localStorage.getItem(FILTERS_PANEL_MODE_KEY);
    return saved === "docked" || saved === "left" ? saved : "floating";
  });

  const handlePanelModeChange = useCallback((mode: FiltersPanelMode) => {
    setPanelMode(mode);
    localStorage.setItem(FILTERS_PANEL_MODE_KEY, mode);
  }, []);
  const [compareOpen, setCompareOpen] = useState(false);
  // Painel "Comparar com uma vaga": fica montado (preserva a vaga escolhida) e o
  // botão da toolbar só mostra/esconde.
  const [compararOpen, setCompararOpen] = useState(false);

  // Debounce busca
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      sessionStorage.setItem(SEARCH_TERM_KEY, searchTerm);
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem(SEARCH_ONLY_NAME_KEY, searchOnlyName.toString());
  }, [searchOnlyName]);

  useEffect(() => {
    localStorage.setItem(SORT_STORAGE_KEY, sortOption);
  }, [sortOption]);

  useEffect(() => {
    localStorage.setItem(PAGE_SIZE_KEY, pageSize.toString());
  }, [pageSize]);

  const searchExact = filters[SEARCH_EXACT_FILTER] === "true";

  // Estratégia de carregamento (server-side por padrão; cache é secundário)

  const {
    pageItems,
    totalCount,
    filteredCount,
    isLoading,
    isFetching: isRefreshing,
    forceRefresh,
    getFreelancersByIds,
  } = useFreelancersData({
    searchTerm: debouncedSearchTerm,
    searchOnlyName,
    filters,
    sortOption,
    page: currentPage,
    pageSize,
  });

  useTrackFreelancerSearch({
    loading: isLoading,
    total: filteredCount,
    filtros: activeFilterNames(filters),
    valoresFiltros: filterValueProperties({ ...filters, busca: debouncedSearchTerm }),
    comBusca: Boolean(debouncedSearchTerm),
    comVaga: Boolean(vagaId),
    pagina: currentPage,
    key: JSON.stringify([vagaId, debouncedSearchTerm, filters, sortOption, currentPage]),
  });

  // Vaga / evaluation group.
  // Candidatos que a vaga JÁ tem vêm do kanban (`vagas_kanban_posicoes`), a fonte
  // de verdade — não do array legado `vagas.freelancers_selecionados`, que hoje
  // cobre menos de um terço dos vínculos reais.
  const { data: candidatosDaVaga = [] } = useQuery({
    queryKey: ["kanban-cards-all", vagaId],
    enabled: !!vagaId,
    queryFn: () => kanbanApi.getCards(vagaId!, true),
  });

  const candidatosExistentes = useMemo(
    () =>
      candidatosDaVaga
        .filter((card) => !card.is_dropped)
        .map((card) => card.freelancer_id),
    [candidatosDaVaga],
  );

  const { data: existingGroupFreelancers = [] } = useQuery({
    queryKey: ["evaluation-group-freelancers", evaluationGroupId],
    queryFn: async () => {
      if (!evaluationGroupId) return [];
      // SELECT direto na tabela: a RPC get_evaluation_group_freelancers usada
      // antes nunca existiu no banco, então o preload falhava silenciosamente
      // e membros existentes nunca apareciam marcados.
      const { data, error } = await supabase
        .from("evaluation_group_freelancers")
        .select("freelancer_id")
        .eq("group_id", parseInt(evaluationGroupId));
      if (error) throw error;
      return (data ?? []).map((linha) => linha.freelancer_id);
    },
    enabled: !!evaluationGroupId,
  });

  // Pré-popula seleção quando entra em modo vaga / group
  useEffect(() => {
    if (candidatosExistentes.length > 0) {
      selection.replace(candidatosExistentes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vagaId, candidatosExistentes.join(",")]);

  useEffect(() => {
    if (evaluationGroupId && existingGroupFreelancers.length > 0) {
      selection.replace(existingGroupFreelancers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluationGroupId, existingGroupFreelancers.join(",")]);

  const totalPages = Math.max(1, Math.ceil(filteredCount / pageSize));
  useEffect(() => {
    // Antes do count resolver, totalPages=1 e o clamp mataria ?page=30.
    if (isLoading || isRefreshing) return;
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [isLoading, isRefreshing, totalPages, currentPage, setCurrentPage]);

  // No modo server a página já vem paginada do banco; no client é a fatia.
  const paginated = pageItems;
  const { data: dispMap } = useDisponibilidadeBatch(paginated.map((f) => f.id));
  const { data: resumoRapidoMap } = useFreelancerResumoRapido(paginated.map((f) => f.id));
  const searchMatchOf = useFreelancerSearchMatch({
    searchTerm: debouncedSearchTerm,
    searchOnlyName,
    searchExact,
    freelancerIds: paginated.map((f) => f.id),
  });
  const candidatoPanel = useSidePanel<CandidatoPanelTab>("candidato");
  useRegistrarPainelDaPagina(candidatoPanel.open);
  const openCandidato = (freelancerId: string) => {
    trackFreelancerView(freelancerId, "painel_candidato", vagaId ? { vaga_id: vagaId } : undefined);
    candidatoPanel.open(freelancerId, vagaId ? "vaga" : undefined);
  };

  // Handlers
  const commitFilters = useCallback(
    (next: FilterValues) => {
      setFilters(next);
      // No modo vaga os filtros sao da sessao (incluindo o default de mostrar
      // inativos); nao sobrescrevem a preferencia do modo normal.
      if (!vagaId) {
        localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(next));
      }
      setCurrentPage(1);
    },
    [vagaId, setCurrentPage],
  );

  const toggleSearchExact = () => {
    const next = { ...filters };
    if (searchExact) delete next[SEARCH_EXACT_FILTER];
    else next[SEARCH_EXACT_FILTER] = "true";
    commitFilters(next);
  };

  const handleFiltersChange = useCallback(
    (next: FilterValues) => {
      const names = activeFilterNames(next);
      trackEvent("filtro.aplicar", {
        contexto: { ...filterValueProperties(next), filtros: names, filtros_n: names.length, origem: "manual", ...filterChanges(activeFilterNames(filters), names) },
      });
      commitFilters(next);
    },
    [commitFilters, filters],
  );

  const handleClearKey = useCallback(
    (key: string) => {
      const next = { ...filters };
      delete next[key];
      handleFiltersChange(next);
    },
    [filters, handleFiltersChange],
  );

  // Chips de disponibilidade clicáveis -> filtro ADITIVO (multi-select): cada clique soma ou
  // remove os valores do grupo/item do conjunto, em vez de substituir a seleção.
  const disponibilidadeFilter = Array.isArray(filters.disponibilidade) ? filters.disponibilidade : undefined;
  const toggleArrayFilter = useCallback(
    (key: string, values: string[]) => {
      const current = Array.isArray(filters[key]) ? (filters[key] as string[]) : [];
      const isActive = values.every((value) => current.includes(value));
      const next = { ...filters };
      const updated = isActive
        ? current.filter((value) => !values.includes(value))
        : [...current, ...values.filter((value) => !current.includes(value))];
      if (updated.length === 0) {
        delete next[key];
      } else {
        next[key] = updated;
      }
      handleFiltersChange(next);
    },
    [filters, handleFiltersChange],
  );
  const handleToggleDisponibilidade = useCallback(
    (values: string[]) => toggleArrayFilter("disponibilidade", values),
    [toggleArrayFilter],
  );
  const handleToggleEixo = useCallback(
    (eixoKey: string, value: string) => toggleArrayFilter(eixoKey, [value]),
    [toggleArrayFilter],
  );
  const handleToggleTemperatura = useCallback(
    (value: string) => toggleArrayFilter("disp_temperatura", [value]),
    [toggleArrayFilter],
  );
  const eixosSelecionados = {
    disp_emprego: Array.isArray(filters.disp_emprego) ? (filters.disp_emprego as string[]) : undefined,
    disp_interesse: Array.isArray(filters.disp_interesse) ? (filters.disp_interesse as string[]) : undefined,
    disp_relacao: Array.isArray(filters.disp_relacao) ? (filters.disp_relacao as string[]) : undefined,
  };
  const temperaturaFilter = Array.isArray(filters.disp_temperatura)
    ? (filters.disp_temperatura as string[])
    : undefined;
  const totalFiltrosAtivos = countActiveFilters(filters);
  const filteredAreas = useMemo(
    () => (Array.isArray(filters.areas) ? (filters.areas as string[]) : []),
    [filters.areas],
  );

  const handleClearAll = useCallback(() => {
    trackEvent("filtro.limpar", { contexto: { filtros_n: totalFiltrosAtivos } });
    // No modo vaga, "limpar" mantem inativos visiveis (default da selecao).
    commitFilters({ is_active: vagaId ? "" : filters.is_active ?? "true" });
  }, [vagaId, filters.is_active, commitFilters, totalFiltrosAtivos]);

  const handleSelectAllOnPage = useCallback(
    (checked: boolean) => {
      const pageIds = paginated.map((f) => f.id);
      if (checked) selection.selectMany(pageIds);
      else pageIds.forEach((id) => selection.set(id, false));
    },
    [paginated, selection],
  );

  const allSelectedOnPage =
    paginated.length > 0 && paginated.every((f) => selection.isSelected(f.id));

  // Sincroniza seleção em massa com backend (modo vaga / grupo). Após o update,
  // verifica o que realmente foi salvo e reporta quais selecionados não entraram
  // (cobre o caso "às vezes não envia todos os selecionados").
  const handleConfirmSelection = useCallback(async () => {
    const ids = Array.from(selection.selected);
    try {
      if (vagaId) {
        // Só cria posição no kanban para quem ainda não é candidato. Quem foi
        // DESmarcado não é removido aqui: sair do processo é "reprovar/descartar"
        // no quadro da vaga, que registra motivo e histórico.
        const { newIds, droppedInPlace, reprovados } = classifyVagaSelection(ids, candidatosDaVaga);
        const rejectedNotice = rejectedCandidatesNotice(reprovados);
        const desmarcados = candidatosExistentes.filter((id) => !selection.selected.has(id));
        const avisoDesmarcados =
          desmarcados.length > 0
            ? `${desmarcados.length} desmarcado(s) seguem candidatos: use Reprovar no quadro para tirar do processo.`
            : "";

        if (newIds.length === 0 && droppedInPlace.length === 0) {
          toast({
            title: "Nada para vincular",
            description:
              [rejectedNotice, avisoDesmarcados].filter(Boolean).join(" ") ||
              "Os selecionados já são candidatos desta vaga.",
          });
          if (!rejectedNotice) navigate(`/vagas/${vagaId}`, { replace: true });
          return;
        }

        let etapaDeEntrada: KanbanEtapa | undefined;
        if (newIds.length > 0) {
          const etapas = await kanbanApi.getEtapas(vagaId);
          etapaDeEntrada = getEtapaDeEntrada(etapas);
          if (!etapaDeEntrada) {
            toast({
              variant: "destructive",
              title: "Vaga sem etapas configuradas",
              description: "Não foi possível vincular candidatos a esta vaga.",
            });
            return;
          }
        }

        const reactivation = await reactivateInVaga(vagaId, droppedInPlace);

        const novos = newIds.length > 0 ? await getFreelancersByIds(newIds) : [];
        const nomePorId = new Map(novos.map((f) => [f.id, f.nome]));
        const envio = etapaDeEntrada
          ? await enviarParaVaga(
              vagaId,
              etapaDeEntrada.id,
              newIds.map((id) => ({ id, nome: nomePorId.get(id) ?? id })),
              KanbanAddOrigin.ListaVaga,
            )
          : { enviados: [], jaExistentes: [], falhas: [] };

        const summary = [
          etapaDeEntrada && envio.enviados.length > 0
            ? `${envio.enviados.length} vinculado(s) à vaga em "${etapaDeEntrada.nome}".`
            : "",
          reactivation.enviados.length > 0 ? `Voltaram para a vaga: ${reactivation.enviados.join(", ")}.` : "",
          rejectedNotice,
          avisoDesmarcados,
        ]
          .filter(Boolean)
          .join(" ");
        const failures = [...envio.falhas, ...reactivation.falhas];

        if (failures.length > 0) {
          toast({
            variant: "destructive",
            title: `${envio.enviados.length + reactivation.enviados.length} vinculados, ${failures.length} com erro`,
            description: `Não entraram: ${failures.map((f) => f.nome).join(", ")}. Tente de novo. ${summary}`.trim(),
          });
          return;
        }

        toast({ title: "Candidatos atualizados", description: summary });
        navigate(`/vagas/${vagaId}`, { replace: true });
        return;
      }
      if (evaluationGroupId && action === "evaluation") {
        const groupId = parseInt(evaluationGroupId);

        // Persiste a seleção inteira no Concluir. Os toggles individuais já
        // salvam na hora via RPC, mas o "selecionar todos da página" só mexe
        // no estado local; sem este sync o grupo ficava vazio com toast de
        // sucesso. O bulk tem ON CONFLICT DO NOTHING, então reenviar quem já
        // está no grupo é inofensivo.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error: addError } = await (supabase as any).rpc(
          "add_freelancers_to_group_bulk",
          {
            p_group_id: groupId,
            p_freelancer_ids: ids,
            p_added_by: user?.id ?? null,
          },
        );
        if (addError) throw addError;

        // Remove só quem estava no grupo quando a tela carregou e foi
        // desmarcado. Se o preload falhou, a lista vem vazia e nada é
        // removido (nunca apagamos membros que o usuário não viu na tela).
        const selecionados = new Set(ids);
        const desmarcados = existingGroupFreelancers.filter(
          (freelancerId) => !selecionados.has(freelancerId),
        );
        for (const freelancerId of desmarcados) {
          const { error: removeError } = await supabase.rpc(
            "remove_freelancer_from_group",
            { p_group_id: groupId, p_freelancer_id: freelancerId },
          );
          if (removeError) throw removeError;
        }

        // Confere o que ficou persistido antes de anunciar sucesso.
        const { data: savedRows, error: readError } = await supabase
          .from("evaluation_group_freelancers")
          .select("freelancer_id")
          .eq("group_id", groupId);
        if (readError) throw readError;
        const savedIds = new Set(
          (savedRows ?? []).map((linha) => linha.freelancer_id),
        );
        const naoSalvos = ids.filter((id) => !savedIds.has(id));

        queryClient.invalidateQueries({
          queryKey: ["evaluation-group-freelancers", evaluationGroupId],
        });

        if (naoSalvos.length > 0) {
          toast({
            variant: "destructive",
            title: `${ids.length - naoSalvos.length} salvos, ${naoSalvos.length} não entraram no grupo`,
            description:
              "Alguns freelancers não foram salvos. Tente concluir novamente.",
          });
          return;
        }

        toast({
          title: "Grupo atualizado",
          description: `${savedIds.size} freelancers no grupo.`,
        });
        navigate(`/avaliacoes/perfil-freelancer/${evaluationGroupId}`, {
          replace: true,
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "Erro ao confirmar seleção",
        description: (err as Error).message,
      });
    }
  }, [
    selection.selected,
    vagaId,
    evaluationGroupId,
    action,
    candidatosExistentes,
    existingGroupFreelancers,
    getFreelancersByIds,
    enviarParaVaga,
    reactivateInVaga,
    candidatosDaVaga,
    queryClient,
    toast,
    navigate,
    kanbanApi,
    user?.id,
  ]);

  // Toggle individual: em modo grupo, faz add/remove imediato no backend
  const handleToggleSelect = useCallback(
    async (id: string) => {
      const wasSelected = selection.isSelected(id);
      selection.toggle(id);

      if (!vagaId && !evaluationGroupId) return;
      try {
        if (evaluationGroupId) {
          const userId = (await supabase.auth.getUser()).data.user?.id;
          if (wasSelected) {
            const { error } = await supabase.rpc("remove_freelancer_from_group", {
              p_group_id: parseInt(evaluationGroupId),
              p_freelancer_id: id,
            });
            if (error) throw error;
          } else {
            const { error } = await supabase.rpc("add_freelancer_to_group", {
              p_group_id: parseInt(evaluationGroupId),
              p_freelancer_id: id,
              p_added_by: userId,
            });
            if (error) throw error;
          }
        }
      } catch (err) {
        // reverte UI em caso de erro
        selection.toggle(id);
        toast({
          variant: "destructive",
          title: "Erro ao atualizar seleção",
          description: (err as Error).message,
        });
      }
    },
    [selection, vagaId, evaluationGroupId, toast],
  );

  // Comparação: resolve os selecionados (até 4) por id — server-side no modo
  // server, já que podem não estar na página atual.
  const [compareFreelancers, setCompareFreelancers] = useState<FreelancerListItem[]>([]);
  useEffect(() => {
    if (!compareOpen) return;
    const ids = Array.from(selection.selected).slice(0, 4);
    let active = true;
    getFreelancersByIds(ids)
      .then((list) => {
        if (active) setCompareFreelancers(list);
      })
      .catch(() => {
        if (active) setCompareFreelancers([]);
      });
    return () => {
      active = false;
    };
  }, [compareOpen, selection.selected, getFreelancersByIds]);

  // Envio em massa para uma vaga (modo normal da listagem): resolve os nomes dos
  // selecionados por id, já que podem não estar na página atual.
  const [enviarParaVagaOpen, setEnviarParaVagaOpen] = useState(false);
  const [freelancersParaEnvio, setFreelancersParaEnvio] = useState<FreelancerParaVaga[]>([]);

  const handleAbrirEnvioParaVaga = useCallback(async () => {
    const lista = await getFreelancersByIds(Array.from(selection.selected));
    setFreelancersParaEnvio(lista.map((f) => ({ id: f.id, nome: f.nome })));
    setEnviarParaVagaOpen(true);
  }, [selection.selected, getFreelancersByIds]);

  // Abre portfolio E LinkedIn de cada selecionado em novas abas. Browsers podem
  // bloquear aberturas em lote — pedimos confirmação antes pra liberar pop-ups.
  const handleOpenLinks = async () => {
    const ids = Array.from(selection.selected);
    const selected = await getFreelancersByIds(ids);
    const tabs: {
      url: string;
      freelancerId: string;
      origem: "linkedin" | "portfolio_externo";
    }[] = [];
    let semNenhum = 0;
    let invalidos = 0;
    selected.forEach((f) => {
      // Só entra na fila o que abre de verdade; link quebrado é contado à parte
      // para o toast não prometer aba que o navegador nunca vai abrir.
      const portfolio = externalUrlHref(f.portfolio_url);
      const linkedin = parseLinkedInUrl(f.linkedin_url).href;
      if (!f.portfolio_url && !f.linkedin_url) {
        semNenhum++;
        return;
      }
      if (f.portfolio_url && !portfolio) invalidos++;
      if (f.linkedin_url && !linkedin) invalidos++;
      if (portfolio)
        tabs.push({ url: portfolio, freelancerId: f.id, origem: "portfolio_externo" });
      if (linkedin) tabs.push({ url: linkedin, freelancerId: f.id, origem: "linkedin" });
    });

    if (tabs.length === 0) {
      toast({
        variant: "destructive",
        title: "Nenhum link para abrir",
        description:
          invalidos > 0
            ? `Nenhum link válido nos selecionados. ${invalidos} link(s) inválido(s).`
            : "Nenhum dos selecionados tem portfolio ou LinkedIn cadastrado.",
      });
      return;
    }
    if (tabs.length > 1) {
      const ok = window.confirm(
        `Vamos abrir ${tabs.length} abas (portfolio + LinkedIn dos selecionados). Se o navegador bloquear, libere pop-ups para este site e tente de novo.`,
      );
      if (!ok) return;
    }
    tabs.forEach((t) => {
      trackFreelancerView(t.freelancerId, t.origem, vagaId ? { vaga_id: vagaId } : undefined);
      openExternalUrl(t.url);
    });
    const avisos = [
      semNenhum > 0 ? `${semNenhum} selecionado(s) sem link cadastrado.` : null,
      invalidos > 0 ? `${invalidos} link(s) inválido(s) foram ignorados.` : null,
    ].filter(Boolean);
    toast({
      title: `${tabs.length} aba${tabs.length > 1 ? "s" : ""} aberta${tabs.length > 1 ? "s" : ""}`,
      description: avisos.length > 0 ? avisos.join(" ") : undefined,
    });
  };

  const modoSelecao = !!(vagaId || evaluationGroupId);

  const panelDocked = filtersPanelOpen && panelMode === "docked";

  // Encaixado, o painel come ~380px: segura a 3ª coluna até o breakpoint seguinte.
  // O painel do candidato come 480px da largura: a grade perde uma coluna por breakpoint.
  const gridClasses = candidatoPanel.isOpen
    ? "grid grid-cols-1 xl:grid-cols-2 gap-3"
    : panelDocked
      ? "grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3"
      : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3";

  const headerTitle = vagaId
    ? "Adicionar Candidatos ao Kanban"
    : evaluationGroupId
      ? "Selecionar Freelancers para Avaliação"
      : "Frilas";

  const headerSubtitle = vagaId
    ? "Selecione uma etapa do kanban para cada candidato"
    : evaluationGroupId
      ? `${selection.size} freelancers no grupo`
      : `${totalCount} cadastrados · ${filteredCount} após filtros`;

  return (
    <div
      className={cn(
        "space-y-4 fade-in pb-24",
        // Painel sobre o menu à esquerda invade ~130px do conteúdo: empurra a
        // listagem pra manter as colunas inteiras visíveis.
        filtersPanelOpen && panelMode === "left" && "lg:pl-34",
      )}
    >
      {vagaId ? (
        <PageHeader
          back={{ fallback: `/vagas/${vagaId}`, label: "Voltar para a vaga" }}
          title={headerTitle}
          subtitle={headerSubtitle}
          actions={
            selection.size > 0 && (
              <Button onClick={handleConfirmSelection} className="gap-2">
                Concluir ({selection.size})
              </Button>
            )
          }
        />
      ) : (
      /* Header: no modo normal o contador fica na mesma linha do título */
      <div className="flex items-start justify-between gap-3">
        <div className={cn("min-w-0", !modoSelecao && "flex flex-wrap items-baseline gap-x-3")}>
          <h1 className="text-2xl font-bold tracking-tight">{headerTitle}</h1>
          <p className="text-muted-foreground text-sm">{headerSubtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {!vagaId && !evaluationGroupId && (
            <Button onClick={() => setNovoFreelancerOpen(true)} className="gap-2">
              <UserPlus className="h-4 w-4" />
              Novo Freelancer
            </Button>
          )}
          {(vagaId || evaluationGroupId) && selection.size > 0 && (
            <Button onClick={handleConfirmSelection} className="gap-2">
              Concluir ({selection.size})
            </Button>
          )}
        </div>
      </div>
      )}

      <NovoFreelancerDialog
        open={novoFreelancerOpen}
        onOpenChange={setNovoFreelancerOpen}
      />

      {/* Search bar + sort + filtros + comparar + view mode */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[280px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Buscar por nome, área, e-mail..."
            className="pl-9 pr-42"
          />
          <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
            <button
              type="button"
              onClick={toggleSearchExact}
              disabled={searchOnlyName}
              aria-pressed={searchExact}
              title="Achar só a palavra inteira, com plural e feminino. Desmarcado, a última palavra vale pelo começo: design acha designer."
              className={cn(
                "rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-40",
                searchExact
                  ? "border-primary/30 bg-primary/10 text-primary"
                  : "border-transparent text-muted-foreground hover:bg-muted",
              )}
            >
              Palavra exata
            </button>
            <button
              type="button"
              onClick={() => setSearchOnlyName(!searchOnlyName)}
              aria-pressed={searchOnlyName}
              title="Buscar apenas pelo nome"
              className={cn(
                "rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors",
                searchOnlyName
                  ? "border-primary/30 bg-primary/10 text-primary"
                  : "border-transparent text-muted-foreground hover:bg-muted",
              )}
            >
              Só nome
            </button>
          </div>
        </div>

        <Select
          value={sortOption}
          onValueChange={(v) => setSortOption(v as FreelancerSortOption)}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(SORT_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          onClick={handleToggleFiltersPanel}
          aria-pressed={filtersPanelOpen}
          className={cn(
            "gap-1.5",
            filtersPanelOpen && "border-primary/30 bg-primary/5",
          )}
          title="Filtros"
        >
          <Filter className="h-4 w-4" />
          <span className="hidden md:inline">Filtros</span>
          {totalFiltrosAtivos > 0 && (
            <span className="rounded-full bg-primary/10 px-1.5 text-[11px] font-semibold text-primary tabular-nums">
              {totalFiltrosAtivos}
            </span>
          )}
        </Button>

        <Button
          variant="outline"
          onClick={() => setCompararOpen((aberto) => !aberto)}
          aria-pressed={compararOpen}
          title="Comparar com vaga"
          className={cn(
            "gap-1.5",
            compararOpen &&
              "border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-700",
          )}
        >
          <Target className="h-4 w-4 text-purple-600" />
          <span className="hidden md:inline">Comparar com vaga</span>
        </Button>

        <div className="flex border rounded-md overflow-hidden">
          {(["grid", "wide", "list"] as const).map((m) => {
            const Icon = m === "grid" ? Grid3x3 : m === "wide" ? Rows3 : List;
            return (
              <button
                key={m}
                type="button"
                onClick={() => setViewMode(m)}
                className={cn(
                  "px-2.5 py-1.5",
                  viewMode === m
                    ? "bg-primary text-primary-foreground"
                    : "bg-background hover:bg-muted",
                )}
                aria-label={`Modo ${m}`}
                title={`Modo ${m}`}
              >
                <Icon className="h-4 w-4" />
              </button>
            );
          })}
        </div>

        <Button
          variant="outline"
          size="icon"
          onClick={() => forceRefresh()}
          disabled={isRefreshing}
          title="Atualizar"
        >
          <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
        </Button>
      </div>

      {/* Chips dos filtros ativos (o botão Filtros mora na toolbar) */}
      <FreelancerFilterChips
        filters={filters}
        onClearKey={handleClearKey}
        onClearAll={handleClearAll}
        onOpenDrawer={() => setFiltersPanelOpen(true)}
        showOpenButton={false}
      />

      {/* Comparar com vaga: ranking pelo motor de recomendação da IA */}
      <FreelancersVagaMatch className={cn(!compararOpen && "hidden")} />

      {/* Selection helper */}
      {paginated.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Checkbox
            checked={allSelectedOnPage}
            onCheckedChange={(v) => handleSelectAllOnPage(!!v)}
            aria-label="Selecionar todos na página"
          />
          <span>
            Selecionar {paginated.length} desta página
            {selection.size > 0 && ` · ${selection.size} selecionados no total`}
          </span>
        </div>
      )}

      {/* Conteúdo; no modo encaixado o painel entra como coluna à direita,
          alinhado com o topo da grade */}
      <div className="flex flex-col-reverse gap-4 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1 space-y-4">
          {isLoading ? (
            <div className={gridClasses}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-44 rounded-lg bg-muted animate-pulse border"
                />
              ))}
            </div>
          ) : paginated.length === 0 ? (
            <div className="border rounded-lg p-10 text-center text-muted-foreground">
              Nenhum freelancer encontrado com os filtros aplicados.
            </div>
          ) : viewMode === "grid" ? (
            <div className={gridClasses}>
              {paginated.map((f) => (
                <FreelancerCardV2
                  key={f.id}
                  freelancer={f}
                  onOpenDetails={() => openCandidato(f.id)}
                  resumoRapido={resumoRapidoMap?.get(f.id) ?? null}
                  vagaId={vagaId ?? undefined}
                  isSelected={selection.isSelected(f.id)}
                  onToggleSelect={handleToggleSelect}
                  disponibilidade={dispMap?.get(f.id) ?? null}
                  filteredAreas={filteredAreas}
                  searchMatch={searchMatchOf(f.id)}
                />
              ))}
            </div>
          ) : viewMode === "wide" ? (
            <div className="grid grid-cols-1 gap-2">
              {paginated.map((f) => (
                <FreelancerCardWide
                  key={f.id}
                  freelancer={f}
                  onOpenDetails={() => openCandidato(f.id)}
                  resumoRapido={resumoRapidoMap?.get(f.id) ?? null}
                  vagaId={vagaId ?? undefined}
                  isSelected={selection.isSelected(f.id)}
                  onToggleSelect={handleToggleSelect}
                  disponibilidade={dispMap?.get(f.id) ?? null}
                  filteredAreas={filteredAreas}
                  searchMatch={searchMatchOf(f.id)}
                />
              ))}
            </div>
          ) : (
            <FreelancerListTable
              freelancers={paginated}
              selectedIds={selection.selected}
              onToggleSelect={handleToggleSelect}
              onSelectAll={handleSelectAllOnPage}
              vagaId={vagaId ?? undefined}
              disponibilidadeMap={dispMap}
              filteredAreas={filteredAreas}
              resumoRapidoMap={resumoRapidoMap}
              searchMatchOf={searchMatchOf}
              onOpenDetails={openCandidato}
            />
          )}

          {/* Paginação (renderiza mesmo com 1 página: o seletor de itens/pg mora aqui) */}
          {filteredCount > 0 && (
            <ListPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setCurrentPage(1);
              }}
            />
          )}
        </div>

        {/* Flutuante à direita some enquanto o painel do candidato ocupa o mesmo lado. */}
        {filtersPanelOpen && !(candidatoPanel.isOpen && panelMode === "floating") && (
          <FreelancerFiltersPanel
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearAll={handleClearAll}
            onClose={handleToggleFiltersPanel}
            mode={panelMode}
            onModeChange={handlePanelModeChange}
            resultCount={filteredCount}
            isFetching={isRefreshing}
            className={
              panelDocked
                ? // Encaixado: coluna sticky com cara própria (fundo tintado +
                  // borda) pra não se confundir com os cards da listagem.
                  "w-full max-h-[70vh] lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:w-[380px] lg:shrink-0 border-primary/20 bg-linear-to-b from-muted/50 to-muted/10 shadow-xs"
                : panelMode === "left"
                  ? // Sobre o menu à esquerda: a listagem fica inteira visível.
                    "fixed bottom-4 left-4 top-4 z-40 w-[min(380px,calc(100vw-2rem))] shadow-2xl"
                  : // Flutuante: fixo sobre a listagem, sem backdrop.
                    "fixed bottom-4 right-4 top-4 z-40 w-[min(380px,calc(100vw-2rem))] shadow-2xl"
            }
            disponibilidadeContent={
              <DisponibilidadeCountChips
                activeEfetiva={disponibilidadeFilter}
                onToggleEfetiva={handleToggleDisponibilidade}
                activeEixos={eixosSelecionados}
                onToggleEixo={handleToggleEixo}
                activeTemperatura={temperaturaFilter}
                onToggleTemperatura={handleToggleTemperatura}
              />
            }
          />
        )}
      </div>

      {/* Multi-select bar */}
      <FreelancerMultiSelectBar
        count={selection.size}
        onClear={selection.clear}
        onCompare={() => setCompareOpen(true)}
        onSendToVaga={vagaId ? handleConfirmSelection : undefined}
        onSendToEvaluationGroup={
          evaluationGroupId ? handleConfirmSelection : undefined
        }
        onEnviarParaVaga={modoSelecao ? undefined : handleAbrirEnvioParaVaga}
        onOpenLinks={handleOpenLinks}
        vagaId={vagaId ?? undefined}
      />

      <EnviarParaVagaDialog
        open={enviarParaVagaOpen}
        onOpenChange={setEnviarParaVagaOpen}
        freelancers={freelancersParaEnvio}
        onSuccess={selection.clear}
      />

      <CandidatoPanel
        freelancerId={candidatoPanel.itemId}
        tab={candidatoPanel.tab}
        onTabChange={candidatoPanel.setTab}
        onClose={candidatoPanel.close}
        listItem={paginated.find((item) => item.id === candidatoPanel.itemId) ?? null}
        vagaId={vagaId}
      />

      {/* Compare drawer */}
      <FreelancerCompareDrawer
        open={compareOpen}
        onOpenChange={setCompareOpen}
        freelancers={compareFreelancers}
        onRemove={(id) => selection.set(id, false)}
      />
    </div>
  );
}
